import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useModalLayer } from '../../lib/modalStack';
import {
  Plus,
  Trash2,
  GripVertical,
  ChevronUp,
  ChevronDown,
  Eye,
  Edit2,
  Check,
  X,
  Type,
  AlignLeft,
  Hash,
  Mail,
  Phone,
  Calendar,
  ChevronDownSquare,
  ListChecks,
  CircleDot,
  CheckSquare,
  Upload,
  ToggleLeft,
  HelpCircle,
  AlertCircle
} from 'lucide-react';
import { FormBuilderField, FormBuilderFieldType } from '../../types';
import { notifyToast } from '../../lib/toastStore';

export interface FormBuilderProps {
  fields: FormBuilderField[];
  onChange: (fields: FormBuilderField[]) => void;
  readOnly?: boolean;
}

export const FIELD_TYPE_LABELS: Record<FormBuilderFieldType, { label: string; icon: any; description: string }> = {
  short_text: { label: 'Short Text', icon: Type, description: 'Single-line text input' },
  long_text: { label: 'Long Text', icon: AlignLeft, description: 'Multi-line text area' },
  number: { label: 'Number', icon: Hash, description: 'Numeric input only' },
  email: { label: 'Email', icon: Mail, description: 'Valid email address' },
  phone: { label: 'Phone', icon: Phone, description: 'Phone number input' },
  date: { label: 'Date', icon: Calendar, description: 'Date picker input' },
  dropdown: { label: 'Dropdown', icon: ChevronDownSquare, description: 'Select one option from dropdown' },
  multi_select: { label: 'Multi-Select', icon: ListChecks, description: 'Select multiple options from dropdown' },
  radio: { label: 'Radio Buttons', icon: CircleDot, description: 'Single selection from list' },
  checkbox: { label: 'Checkboxes', icon: CheckSquare, description: 'Multiple selection checkboxes' },
  file_upload: { label: 'File Upload', icon: Upload, description: 'File attachment slot' },
  yes_no: { label: 'Yes/No', icon: ToggleLeft, description: 'Binary Yes or No choice' }
};

export const CHOICE_BASED_TYPES: FormBuilderFieldType[] = ['dropdown', 'multi_select', 'radio', 'checkbox'];

export function parseRawOptions(options?: any): string[] {
  if (!options) return [];
  if (Array.isArray(options)) {
    return options.map(opt => typeof opt === 'string' ? opt : (opt.label || opt.value || ''));
  }
  if (typeof options === 'string') {
    return options.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
  }
  return [];
}

export default function FormBuilder({ fields, onChange, readOnly = false }: FormBuilderProps) {
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const previewZIndex = useModalLayer(showPreviewModal, () => setShowPreviewModal(false));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // New field state
  const [newLabel, setNewLabel] = useState('');
  const [newType, setNewType] = useState<FormBuilderFieldType>('short_text');
  const [newRequired, setNewRequired] = useState(true);
  const [newPlaceholder, setNewPlaceholder] = useState('');
  const [newHelpText, setNewHelpText] = useState('');
  const [newOptionsText, setNewOptionsText] = useState('');

  // Active edit state
  const [editLabel, setEditLabel] = useState('');
  const [editType, setEditType] = useState<FormBuilderFieldType>('short_text');
  const [editRequired, setEditRequired] = useState(true);
  const [editPlaceholder, setEditPlaceholder] = useState('');
  const [editHelpText, setEditHelpText] = useState('');
  const [editOptionsText, setEditOptionsText] = useState('');

  const handleAddField = () => {
    if (!newLabel.trim()) {
      notifyToast('warning', 'Please enter a question or field name.');
      return;
    }

    let parsedOptions: string[] = [];
    if (CHOICE_BASED_TYPES.includes(newType)) {
      parsedOptions = newOptionsText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      if (parsedOptions.length === 0) {
        notifyToast('warning', 'Please add at least one option for choice-based question.');
        return;
      }
    } else if (newType === 'yes_no') {
      parsedOptions = ['Yes', 'No'];
    }

    const newField: FormBuilderField = {
      id: `field_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      label: newLabel.trim(),
      type: newType,
      required: newRequired,
      placeholder: newPlaceholder.trim() || undefined,
      helpText: newHelpText.trim() || undefined,
      options: parsedOptions.length > 0 ? parsedOptions : undefined
    };

    onChange([...fields, newField]);

    // Reset form
    setNewLabel('');
    setNewType('short_text');
    setNewRequired(true);
    setNewPlaceholder('');
    setNewHelpText('');
    setNewOptionsText('');
  };

  const startEditing = (field: FormBuilderField) => {
    setEditingFieldId(field.id);
    setEditLabel(field.label);
    setEditType(field.type);
    setEditRequired(field.required ?? true);
    setEditPlaceholder(field.placeholder || '');
    setEditHelpText(field.helpText || '');
    const currentOpts = parseRawOptions(field.options);
    setEditOptionsText(currentOpts.join('\n'));
  };

  const handleSaveEdit = (fieldId: string) => {
    if (!editLabel.trim()) {
      notifyToast('warning', 'Question label cannot be empty.');
      return;
    }

    let parsedOptions: string[] = [];
    if (CHOICE_BASED_TYPES.includes(editType)) {
      parsedOptions = editOptionsText
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
      if (parsedOptions.length === 0) {
        notifyToast('warning', 'Please add at least one option for choice-based question.');
        return;
      }
    } else if (editType === 'yes_no') {
      parsedOptions = ['Yes', 'No'];
    }

    const updated = fields.map(f => {
      if (f.id === fieldId) {
        return {
          ...f,
          label: editLabel.trim(),
          type: editType,
          required: editRequired,
          placeholder: editPlaceholder.trim() || undefined,
          helpText: editHelpText.trim() || undefined,
          options: parsedOptions.length > 0 ? parsedOptions : undefined
        };
      }
      return f;
    });

    onChange(updated);
    setEditingFieldId(null);
  };

  const handleDeleteField = (fieldId: string) => {
    onChange(fields.filter(f => f.id !== fieldId));
    if (editingFieldId === fieldId) {
      setEditingFieldId(null);
    }
  };

  const moveField = (index: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    const newFields = [...fields];
    const [moved] = newFields.splice(index, 1);
    newFields.splice(targetIndex, 0, moved);
    onChange(newFields);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    const newFields = [...fields];
    const item = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, item);
    setDraggedIndex(index);
    onChange(newFields);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between pb-2 border-b border-slate-200">
        <div>
          <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
            Interactive Form Builder
            <span className="text-xs font-normal text-slate-500">({fields.length} question{fields.length === 1 ? '' : 's'})</span>
          </h4>
          <p className="text-[11px] text-slate-500">Configure questions, input formats, requirements, and options.</p>
        </div>
        {fields.length > 0 && (
          <button
            type="button"
            onClick={() => setShowPreviewModal(true)}
            className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg flex items-center gap-1.5 transition-colors"
          >
            <Eye className="w-3.5 h-3.5 text-slate-600" />
            Preview Form
          </button>
        )}
      </div>

      {/* Field List / Builder Workspace */}
      <div className="space-y-3">
        {fields.length === 0 ? (
          <div className="p-6 text-center bg-slate-50 border-2 border-dashed border-slate-200 rounded-xl">
            <HelpCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs font-bold text-slate-600">No Questions Added Yet</p>
            <p className="text-[11px] text-slate-400 mt-0.5">Use the question builder below to add fields to this form.</p>
          </div>
        ) : (
          fields.map((field, index) => {
            const isEditing = editingFieldId === field.id;
            const TypeIcon = FIELD_TYPE_LABELS[field.type]?.icon || Type;
            const opts = parseRawOptions(field.options);

            return (
              <div
                key={field.id}
                draggable={!readOnly && !isEditing}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragEnd={handleDragEnd}
                className={`p-3.5 bg-white border rounded-xl transition-all ${
                  isEditing ? 'border-brand ring-2 ring-brand/10 shadow-md' : 'border-slate-200 hover:border-slate-300 shadow-sm'
                }`}
              >
                {!isEditing ? (
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 flex-1 min-w-0">
                      {!readOnly && (
                        <div className="cursor-grab active:cursor-grabbing pt-0.5 text-slate-300 hover:text-slate-500">
                          <GripVertical className="w-4 h-4" />
                        </div>
                      )}
                      <div className="p-1.5 bg-slate-100 rounded-lg text-slate-600 shrink-0">
                        <TypeIcon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-slate-900">{index + 1}. {field.label}</span>
                          {field.required ? (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-rose-50 text-rose-600 border border-rose-200 rounded">
                              Required
                            </span>
                          ) : (
                            <span className="px-1.5 py-0.5 text-[10px] font-semibold bg-slate-100 text-slate-500 rounded">
                              Optional
                            </span>
                          )}
                          <span className="px-2 py-0.5 text-[10px] font-bold bg-blue-50 text-blue-700 rounded-full">
                            {FIELD_TYPE_LABELS[field.type]?.label || field.type}
                          </span>
                        </div>

                        {field.helpText && (
                          <p className="text-[11px] text-slate-500 mt-1 italic">{field.helpText}</p>
                        )}

                        {field.placeholder && (
                          <p className="text-[11px] text-slate-400 mt-0.5">Placeholder: &quot;{field.placeholder}&quot;</p>
                        )}

                        {opts.length > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-bold uppercase text-slate-400">Choices:</span>
                            {opts.map((opt, oIdx) => (
                              <span key={oIdx} className="px-2 py-0.5 text-[10px] bg-slate-100 text-slate-700 rounded-md border border-slate-200">
                                {opt}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {!readOnly && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          disabled={index === 0}
                          onClick={() => moveField(index, 'up')}
                          className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 rounded"
                          title="Move Up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          disabled={index === fields.length - 1}
                          onClick={() => moveField(index, 'down')}
                          className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 rounded"
                          title="Move Down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => startEditing(field)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors ml-1"
                          title="Edit Question"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteField(field.id)}
                          className="p-1.5 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                          title="Delete Question"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Edit Form Panel */
                  <div className="space-y-3 p-1">
                    <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                      <p className="text-xs font-bold text-brand flex items-center gap-1">
                        <Edit2 className="w-3 h-3" /> Edit Question #{index + 1}
                      </p>
                      <button
                        type="button"
                        onClick={() => setEditingFieldId(null)}
                        className="text-slate-400 hover:text-slate-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                          Question / Field Name *
                        </label>
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-brand font-semibold"
                          placeholder="e.g. Please enter your full legal name"
                        />
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                          Input Type *
                        </label>
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value as FormBuilderFieldType)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-brand font-semibold"
                        >
                          {Object.entries(FIELD_TYPE_LABELS).map(([typeKey, cfg]) => (
                            <option key={typeKey} value={typeKey}>
                              {cfg.label} ({cfg.description})
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="flex items-end pb-1">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={editRequired}
                            onChange={(e) => setEditRequired(e.target.checked)}
                            className="w-4 h-4 text-brand rounded border-slate-300 focus:ring-brand"
                          />
                          <span className="text-xs font-bold text-slate-700">Mandatory / Required Field</span>
                        </label>
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                          Placeholder Text
                        </label>
                        <input
                          type="text"
                          value={editPlaceholder}
                          onChange={(e) => setEditPlaceholder(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-brand"
                          placeholder="e.g. John Doe"
                        />
                      </div>

                      <div>
                        <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                          Help Text / Instructions
                        </label>
                        <input
                          type="text"
                          value={editHelpText}
                          onChange={(e) => setEditHelpText(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-brand"
                          placeholder="e.g. As shown on your passport"
                        />
                      </div>

                      {CHOICE_BASED_TYPES.includes(editType) && (
                        <div className="sm:col-span-2">
                          <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                            Choices / Options * <span className="text-slate-400 font-normal normal-case">(One option per line)</span>
                          </label>
                          <textarea
                            rows={3}
                            value={editOptionsText}
                            onChange={(e) => setEditOptionsText(e.target.value)}
                            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-brand font-mono"
                            placeholder={"Option 1\nOption 2\nOption 3"}
                          />
                        </div>
                      )}
                    </div>

                    <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                      <button
                        type="button"
                        onClick={() => setEditingFieldId(null)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(field.id)}
                        className="px-3 py-1.5 bg-brand hover:bg-brand-dark text-white text-xs font-semibold rounded-lg flex items-center gap-1 shadow-sm"
                      >
                        <Check className="w-3 h-3" /> Save Changes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Add New Question Section */}
      {!readOnly && (
        <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
          <h5 className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
            <Plus className="w-4 h-4 text-brand" /> Add New Question
          </h5>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                Question / Field Name *
              </label>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-xl outline-none focus:border-brand font-semibold"
                placeholder="e.g. What is your preferred start date?"
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                Input Type *
              </label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as FormBuilderFieldType)}
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl bg-white outline-none focus:border-brand font-semibold"
              >
                {Object.entries(FIELD_TYPE_LABELS).map(([typeKey, cfg]) => (
                  <option key={typeKey} value={typeKey}>
                    {cfg.label} ({cfg.description})
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end pb-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newRequired}
                  onChange={(e) => setNewRequired(e.target.checked)}
                  className="w-4 h-4 text-brand rounded border-slate-300 focus:ring-brand"
                />
                <span className="text-xs font-bold text-slate-700">Required Field</span>
              </label>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                Placeholder Text <span className="text-slate-400 font-normal normal-case">(Optional)</span>
              </label>
              <input
                type="text"
                value={newPlaceholder}
                onChange={(e) => setNewPlaceholder(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-xl outline-none focus:border-brand"
                placeholder="e.g. Select date..."
              />
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                Help Text / Instructions <span className="text-slate-400 font-normal normal-case">(Optional)</span>
              </label>
              <input
                type="text"
                value={newHelpText}
                onChange={(e) => setNewHelpText(e.target.value)}
                className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-xl outline-none focus:border-brand"
                placeholder="e.g. Choose from available dates"
              />
            </div>

            {CHOICE_BASED_TYPES.includes(newType) && (
              <div className="sm:col-span-2">
                <label className="text-[11px] font-bold text-slate-600 uppercase block mb-1">
                  Choices / Options * <span className="text-slate-400 font-normal normal-case">(One option per line)</span>
                </label>
                <textarea
                  rows={3}
                  value={newOptionsText}
                  onChange={(e) => setNewOptionsText(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-xl outline-none focus:border-brand font-mono"
                  placeholder={"Option A\nOption B\nOption C"}
                />
              </div>
            )}
          </div>

          <div className="flex justify-end pt-1">
            <button
              type="button"
              onClick={handleAddField}
              className="px-4 py-2 bg-brand hover:bg-brand-dark text-white text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Question to Form
            </button>
          </div>
        </div>
      )}

      {/* Live Form Preview Modal */}
      {showPreviewModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" style={{ zIndex: previewZIndex }}>
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-200 space-y-4 animate-scaleUp">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-brand/10 text-brand rounded-xl">
                  <Eye className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900">Form Preview</h3>
                  <p className="text-xs text-slate-500">Exact layout as applicants will see it on public submission.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {fields.map((field, idx) => {
                const opts = parseRawOptions(field.options);
                return (
                  <div key={field.id} className="p-3.5 bg-slate-50/70 border border-slate-200 rounded-xl space-y-1.5">
                    <label className="text-xs font-bold text-slate-800 block">
                      {idx + 1}. {field.label} {field.required && <span className="text-rose-500">*</span>}
                    </label>
                    {field.helpText && (
                      <p className="text-[11px] text-slate-500">{field.helpText}</p>
                    )}

                    {field.type === 'short_text' && (
                      <input
                        type="text"
                        disabled
                        placeholder={field.placeholder || 'Short text response...'}
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'long_text' && (
                      <textarea
                        disabled
                        rows={3}
                        placeholder={field.placeholder || 'Long text response...'}
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'number' && (
                      <input
                        type="number"
                        disabled
                        placeholder={field.placeholder || '0'}
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'email' && (
                      <input
                        type="email"
                        disabled
                        placeholder={field.placeholder || 'name@example.com'}
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'phone' && (
                      <input
                        type="tel"
                        disabled
                        placeholder={field.placeholder || '+1 (555) 000-0000'}
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'date' && (
                      <input
                        type="date"
                        disabled
                        className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75"
                      />
                    )}

                    {field.type === 'dropdown' && (
                      <select disabled className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75">
                        <option value="">{field.placeholder || '-- Select an option --'}</option>
                        {opts.map((o, oIdx) => (
                          <option key={oIdx} value={o}>{o}</option>
                        ))}
                      </select>
                    )}

                    {field.type === 'multi_select' && (
                      <select disabled multiple size={Math.min(4, opts.length || 2)} className="w-full px-3 py-2 text-xs border border-slate-200 bg-white rounded-lg opacity-75">
                        {opts.map((o, oIdx) => (
                          <option key={oIdx} value={o}>{o}</option>
                        ))}
                      </select>
                    )}

                    {field.type === 'radio' && (
                      <div className="space-y-1 pt-1">
                        {opts.map((o, oIdx) => (
                          <label key={oIdx} className="flex items-center gap-2 text-xs text-slate-700 opacity-75 cursor-not-allowed">
                            <input type="radio" disabled name={`preview_radio_${field.id}`} /> {o}
                          </label>
                        ))}
                      </div>
                    )}

                    {(field.type === 'checkbox' || field.type === 'yes_no') && (
                      <div className="space-y-1 pt-1">
                        {(field.type === 'yes_no' ? ['Yes', 'No'] : opts).map((o, oIdx) => (
                          <label key={oIdx} className="flex items-center gap-2 text-xs text-slate-700 opacity-75 cursor-not-allowed">
                            <input type="checkbox" disabled /> {o}
                          </label>
                        ))}
                      </div>
                    )}

                    {field.type === 'file_upload' && (
                      <div className="p-3 border-2 border-dashed border-slate-200 rounded-lg bg-white text-center opacity-75">
                        <Upload className="w-5 h-5 text-slate-400 mx-auto mb-1" />
                        <p className="text-xs text-slate-500 font-semibold">Click or drag file to upload</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-dark text-white text-xs font-bold rounded-xl"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
